import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface PowensPendingConnection {
  id: string;
  connector_name: string | null;
  /** Null while healthy; otherwise the Powens state blocking the sync. */
  state: string | null;
  powens_connection_id: number;
  /** No transaction older than this is ever imported. */
  transactions_from_date: string | null;
  created_at: string;
}

/** Stop polling after this long: a bank that has not answered by then needs the webhook. */
const POLL_CEILING_MS = 5 * 60 * 1000;
const POLL_INTERVAL_MS = 8000;

/**
 * Bank connections that exist but have no account yet.
 *
 * Powens keeps pulling from the bank for 30 to 45 seconds after the webview
 * hands the browser back, so a user who has just linked a bank lands on an
 * accounts page that still shows nothing — which reads as a failed connection.
 * The connection row is written before the data arrives precisely so the page
 * can say "this is coming" instead of showing a gap.
 *
 * While something is pending the hook polls and asks the accounts to reload, so
 * the placeholder turns into the real account on its own.
 */
export function usePowensPendingConnections(accounts: { powens_connection_id?: string | null }[]) {
  const { user } = useAuth();
  const [connections, setConnections] = useState<PowensPendingConnection[]>([]);
  const startedAt = useRef(Date.now());

  const load = useCallback(async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('powens_connections')
      .select('id, connector_name, state, powens_connection_id, transactions_from_date, created_at')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading Powens connections:', error);
      return;
    }

    setConnections((data ?? []) as PowensPendingConnection[]);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const pending = useMemo(() => {
    const linked = new Set(
      accounts.map((account) => account.powens_connection_id).filter(Boolean) as string[]
    );
    return connections.filter((connection) => !linked.has(connection.id));
  }, [connections, accounts]);

  // Only a connection that is still working is worth waiting on. One blocked on
  // the user (SCARequired, webauthRequired, wrongpass) will never resolve by
  // itself, so polling for it would just spin until the ceiling.
  const awaitingData = useMemo(() => pending.filter((c) => !c.state), [pending]);

  useEffect(() => {
    if (awaitingData.length === 0) return;
    if (Date.now() - startedAt.current > POLL_CEILING_MS) return;

    const timer = setInterval(() => {
      if (Date.now() - startedAt.current > POLL_CEILING_MS) {
        clearInterval(timer);
        return;
      }
      load();
      window.dispatchEvent(new CustomEvent('accounts-need-refresh'));
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [awaitingData.length, load]);

  // The earliest boundary across the user's banks. An aggregated account with
  // no transactions is not necessarily still loading: by design nothing older
  // than this date is ever imported, and saying "importing" there would be a
  // lie the user waits on forever.
  const oldestImportedFrom = useMemo(() => {
    const dates = connections
      .map((connection) => connection.transactions_from_date)
      .filter(Boolean) as string[];
    return dates.length > 0 ? dates.sort()[0] : null;
  }, [connections]);

  return {
    connections,
    pendingConnections: pending,
    oldestImportedFrom,
    reload: load,
  };
}
